// Shared display helpers for the attribution feasibility page and its modals.

export const VERDICTS = {
  FEASIBLE: { label: 'Go', css: 'fz-pill-go', hint: 'Clears the pipeline floor (2 exposed / 5 control converters per draw).' },
  REVIEW: { label: 'Review', css: 'fz-pill-review', hint: "Short of the floor on the estimate, but within the back-test's error. Don't decline; re-check on the flight's first weeks." },
  NOT_FEASIBLE: { label: 'Not feasible', css: 'fz-pill-no', hint: 'Misses the floor even at the optimistic end of the back-test error.' },
  NO_CONVERSION_HISTORY: { label: 'Needs pixel data', css: 'fz-pill-info', hint: 'The pixel has no usable data before the flight; the reasons give the conversion rate it needs.' },
  INSUFFICIENT_HISTORY: { label: 'No inventory history', css: 'fz-pill-muted', hint: 'Too few of the units were ever matched to project from.' },
  NO_INVENTORY: { label: 'No inventory', css: 'fz-pill-muted', hint: 'The campaigns have no frames or assets.' },
  NO_CONVERSION_TARGETS: { label: 'No places or pixels', css: 'fz-pill-muted', hint: 'The study has no places or pixels attached.' },
  ERROR: { label: 'Error', css: 'fz-pill-no', hint: '' },
};

export const STATUSES = {
  PENDING: { label: 'Queued', css: 'fz-pill-queued' },
  RUNNING: { label: 'Running', css: 'fz-pill-queued' },
  ERROR: { label: 'Failed', css: 'fz-pill-no' },
};

/** The pill shown for a row: the verdict when done, else the status */
export function pillFor(row) {
  if (!row) return { label: '—', css: 'fz-pill-muted' };
  if (row.status === 'DONE') return VERDICTS[row.verdict] || { label: row.verdict || 'Done', css: 'fz-pill-muted' };
  return STATUSES[row.status] || { label: row.status || '—', css: 'fz-pill-muted' };
}

export function isActive(row) {
  return row && (row.status === 'PENDING' || row.status === 'RUNNING');
}

export function formatNumber(value, digits = 0) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  if (Math.abs(n) >= 10000) return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  return n.toLocaleString('en-US', { maximumFractionDigits: digits });
}

export function formatPercent(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Math.round(Number(value) * 100)}%`;
}

/** Snowflake TIMESTAMP_LTZ strings come back as "2026-10-29 10:13:51.794 -0700" */
export function parseTimestamp(value) {
  if (!value) return null;
  const iso = String(value).replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?) ([+-]\d{2})(\d{2})$/, '$1T$2$3:$4');
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function timeAgo(value, now = Date.now()) {
  const date = parseTimestamp(value);
  if (!date) return '—';
  const seconds = Math.max(0, Math.round((now - date.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTime(value) {
  const date = parseTimestamp(value);
  return date ? date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
}

export function duration(fromValue, toValue, now = Date.now()) {
  const from = parseTimestamp(fromValue);
  if (!from) return '';
  const to = parseTimestamp(toValue);
  const seconds = Math.max(0, Math.round(((to ? to.getTime() : now) - from.getTime()) / 1000));
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
}

/*
 * Run cost. Checks run on ATTRIBUTION_PIPELINE__WH_L (Large, 8 credits/h). Snowflake's per-query attribution
 * for the first prod checks came to 1.15-1.37x run time x 8 credits/h, so the estimate uses 1.3x.
 * It is an estimate from run time, not a billed figure.
 */
export const CHECK_CREDITS_PER_HOUR = 8;
export const CHECK_CREDIT_OVERHEAD = 1.3;
export const USD_PER_CREDIT = 1.76;

/** Seconds from picked up to finished; null while queued or running */
export function runSeconds(row) {
  const from = parseTimestamp(row && row.startedAt);
  const to = parseTimestamp(row && row.finishedAt);
  if (!from || !to) return null;
  return Math.max(0, Math.round((to.getTime() - from.getTime()) / 1000));
}

export function formatSeconds(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
}

export function estimateCredits(seconds) {
  if (seconds === null || seconds === undefined) return null;
  return (seconds / 3600) * CHECK_CREDITS_PER_HOUR * CHECK_CREDIT_OVERHEAD;
}

/** "≈0.33 credits · $0.58" */
export function formatCost(seconds) {
  const credits = estimateCredits(seconds);
  if (credits === null) return '—';
  return `≈${credits.toFixed(2)} credits · $${(credits * USD_PER_CREDIT).toFixed(2)}`;
}

/** The POST body that queues the same check again, from the stored request */
export function rerunBody(request) {
  const body = {};
  if (!request) return body;
  if (request.studyId) body.studyId = request.studyId;
  const campaignIds = request.campaignIds || (request.campaignId ? [request.campaignId] : null);
  if (campaignIds) body.campaignIds = campaignIds;
  ['placeIds', 'pixelIds', 'customerId', 'flightStart', 'flightEnd'].forEach((key) => {
    if (request[key] !== undefined && request[key] !== null) body[key] = request[key];
  });
  return body;
}

/** "Boost Mobile Footfall" or "Campaign 244550 · 3 places" */
export function subjectTitle(row) {
  if (row.studyName) return row.studyName;
  if (row.studyId) return `Study ${row.studyId}`;
  const campaigns = row.campaigns || [];
  if (campaigns.length === 1) return campaigns[0].campaignRef || `Campaign ${campaigns[0].campaignId}`;
  return `${campaigns.length} campaigns`;
}

export function subjectDetail(row) {
  const parts = [];
  if (row.studyId) parts.push(`Study ${row.studyId}`);
  const campaigns = row.campaigns || [];
  if (campaigns.length) parts.push(campaigns.length === 1 ? `Campaign ${campaigns[0].campaignId}` : `${campaigns.length} campaigns`);
  const places = (row.placeIds || []).length;
  const pixels = (row.pixelIds || []).length;
  if (places) parts.push(`${places} place${places === 1 ? '' : 's'}`);
  if (pixels) parts.push(`${pixels} pixel${pixels === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

export function typeLabel(studyType) {
  if (studyType === 'WEB') return 'Web';
  if (studyType === 'LOCATION') return 'Footfall';
  return '';
}

/** Helios wraps every response as { data: ... } */
export function unwrap(response) {
  return response && Object.prototype.hasOwnProperty.call(response, 'data') ? response.data : response;
}

export function errorMessage(error, fallback) {
  if (!error) return fallback;
  let body = error.response && error.response.body;
  if (!body && error.response && error.response.text) {
    try {
      body = JSON.parse(error.response.text);
    } catch (e) {
      body = null;
    }
  }
  return (body && body.message) || error.message || fallback;
}
